import * as MessageService from '~/services/message';
import { DetailType } from '@portal/models/enumerators';
import { Dispatch } from 'redux';
import TransactionApi from '~/repositories/transaction';

import * as ActionTypes from './actionTypes';
import {
  decreaseDetailsLoading,
  increaseDetailsLoading,
  LoadingAction,
} from './loading';

export type SetTransactionDetailAction = {
  type: string;
  payload: {
    transaction?: bank.Transaction | null;
    type?: DetailType | null;
    error?: Error | null;
  };
};

export const setDetails = (payload: SetTransactionDetailAction['payload']) => ({
  type: ActionTypes.SET_DETAILS,
  payload,
});

export const unsetDetails = () => ({
  type: ActionTypes.UNSET_DETAILS,
});

export type SetDetailsErrorAction = { type: string; payload: Error | null };

export const setDetailsError = (error: SetDetailsErrorAction['payload']) => ({
  type: ActionTypes.SET_DETAILS_ERROR,
  payload: error,
});

export const getTransactionDetails =
  (id: string, paymentAccountId: string | null) =>
  async (
    dispatch: Dispatch<
      SetTransactionDetailAction | LoadingAction | SetDetailsErrorAction
    >
  ) => {
    dispatch(increaseDetailsLoading());
    try {
      const params = paymentAccountId
        ? { headers: { 'payment-account-id': paymentAccountId } }
        : undefined;
      const transaction = await TransactionApi.getDetails(id, params);
      dispatch({
        type: ActionTypes.SET_DETAILS,
        payload: {
          error: null,
          transaction,
          type: null,
        },
      });
    } catch (err) {
      if (err instanceof Error) {
        dispatch(setDetailsError(err));
        MessageService.error(err.message, { originalError: err });
      }
      dispatch(unsetDetails());
    } finally {
      dispatch(decreaseDetailsLoading());
    }
  };

export const getTransactionDetailsScheduled =
  (id: string) =>
  async (
    dispatch: Dispatch<
      SetTransactionDetailAction | LoadingAction | SetDetailsErrorAction
    >
  ) => {
    dispatch(increaseDetailsLoading());
    try {
      const transaction = await TransactionApi.getScheduledTransaction(id);

      const transactionFormatted: bank.Transaction = {
        id: transaction.id,
        type: transaction.type,
        status: transaction.status,
        amount: transaction.amount,
        description: transaction.description ?? undefined,
        transactionScheduleAt: transaction.transactionScheduleAt,

        shared: {
          origin: {
            userName: transaction.account.name,
            bankName: transaction.paymentAccount.paymentAccountName || '',
            userTaxIdentifier: transaction.account.taxIdentifier,
            userBankAccount: transaction.paymentAccount.bankAccount,
            userBankBranch: transaction.paymentAccount.bankBranchAccount,
          },
          destiny: {
            userName: transaction.favored?.alias || '',
            bankName:
              transaction.favored?.bankUserName ||
              transaction.favored?.bankCode ||
              '',
            userTaxIdentifier: transaction.favored?.taxIdentifier || '',
            userBankAccount: transaction.favored?.bankAccount || '',
            userBankBranch: transaction.favored?.bankAgency || '',
          },
        },

        ...(transaction.account && { account: transaction.account }),
        ...(transaction.paymentAccount && {
          paymentAccount: transaction.paymentAccount,
        }),
        ...(transaction.favored && { favored: transaction.favored }),
      };

      dispatch({
        type: ActionTypes.SET_DETAILS,
        payload: {
          error: null,
          transaction: transactionFormatted,
          type: null,
        },
      });
    } catch (err) {
      if (err instanceof Error) {
        dispatch(setDetailsError(err));
        MessageService.error(err.message, { originalError: err });
      }
      dispatch(unsetDetails());
    } finally {
      dispatch(decreaseDetailsLoading());
    }
  };
